import React, { useRef, useEffect, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/effect-creative";
import { EffectCreative, Autoplay } from "swiper/modules";

export default function NewSwiper({ data = [] }) {
  const swiperRef = useRef(null);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const totalImages = data.length;

  // Preload background images and count loaded images
  useEffect(() => {
    if (!data || data.length === 0) return;

    let cancelled = false;
    let loaded = 0;

    data.forEach((item) => {
      const img = new Image();
      img.src = item.url;
      img.onload = () => {
        if (cancelled) return;
        loaded += 1;
        setImagesLoaded(loaded);
      };
      img.onerror = () => {
        if (cancelled) return;
        loaded += 1; // count error as "done" so we don't hang
        setImagesLoaded(loaded);
      };
    });

    return () => {
      cancelled = true;
    };
  }, [data]);

  // Start autoplay once swiper exists AND either:
  // - all images loaded, OR
  // - fallback timeout reached (in case images are slow)
  useEffect(() => {
    const swiper = swiperRef.current?.swiper;
    if (!swiper) return;

    // If all images are already loaded -> start immediately
    if (totalImages === 0 || imagesLoaded >= totalImages) {
      // if autoplay was disabled, start it
      if (swiper.autoplay && swiper.autoplay.running === false) {
        swiper.autoplay.start();
      }
      return;
    }

    // Otherwise wait for either imagesLoaded to reach totalImages OR fallback timeout
    let fallback = setTimeout(() => {
      if (swiper.autoplay && swiper.autoplay.running === false) {
        swiper.autoplay.start();
      }
    }, 500); // 500ms fallback (adjust if needed)

    return () => clearTimeout(fallback);
  }, [imagesLoaded, totalImages]);

  // Also ensure autoplay start right after mount in case swiper becomes available later
  useEffect(() => {
    const attemptStart = () => {
      const s = swiperRef.current?.swiper;
      if (s && s.autoplay && s.autoplay.running === false) {
        s.autoplay.start();
      }
    };

    // try immediately and once more after a tiny delay
    attemptStart();
    const t = setTimeout(attemptStart, 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <Swiper
      ref={swiperRef}
      modules={[EffectCreative, Autoplay]}
      effect="creative"
      grabCursor={true}
      loop={true}
      autoplay={{
        delay: 5000,
        disableOnInteraction: false,
        waitForTransition: false,
        pauseOnMouseEnter: false, // change to true if you want pause on hover
      }}
      creativeEffect={{
        prev: { shadow: true, translate: [0, 0, -400] },
        next: { translate: ["100%", 0, 0] },
      }}
      onSwiper={(s) => {
        // ensure autoplay has been started if conditions met
        if (s && s.autoplay && s.autoplay.running === false) {
          // small defer so Swiper internals finish init
          setTimeout(() => {
            try {
              s.autoplay.start();
            } catch (e) {
              // ignore if cannot start yet
            }
          }, 50);
        }
      }}
      className="mySwiper"
    >
      {data.map((item, index) => (
        <SwiperSlide key={index}>
          <div
            className="w-full h-[500px]"
            style={{
              backgroundImage: `url(${item.url})`,
              backgroundRepeat: "no-repeat",
              backgroundSize: "cover",
              backgroundPosition: "bottom",
              position: "relative",
            }}
          />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}
